// src/pages/account/Preferences.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import './Preferences.css';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { BASE_URL } from '../../utils/config';
import { Role } from '../../utils/enums'; // Import the Role enum
import { apiFetch } from '../../utils/apiClient';
import EmailVerification from '../../components/verification/EmailVerification'; // Import the new component
import PhoneVerification from '../../components/verification/PhoneVerification'; // Import the new component
import InvestmentProfileModal from '../../components/modals/InvestmentProfileModal'; // Import the modal
import { useLanguage } from '../../hooks/useLanguage';
import BecomeSellerModal from '../../components/modals/BecomeSellerModal';

const Preferences = () => {
  const { user, updateUser, logoutUser } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext); // Get addNotification
  const { t } = useTranslation();
  const lang = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const returnToPath = location.state?.returnTo;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    itemsPerPage: 12,
    subscribe: false,
    currency: 'ILS', // Add currency
    phoneNumber: '',
    phoneCountryCode: '',
    reportLanguage: '', // Renamed and set to lowercase
    investmentProfile: {},
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sellerRequestStatus, setSellerRequestStatus] = useState(''); // For "Become a Seller" feedback
  const [isRequestingSeller, setIsRequestingSeller] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  const [isBecomeSellerModalOpen, setIsBecomeSellerModalOpen] = useState(false);
  //  const autoDetectLanguageRan = useRef(false);

  // --- Effects ---

  // Effect to show a welcome message after email verification
  useEffect(() => {
    if (location.state?.from === 'email-verification') {
      // Only prompt to verify phone if it's not already verified.
      if (!user.isPhoneVerified) {
        addNotification(t('preferencesPage.welcomeCompleteProfile'), 'info', 1000);
      } else {
        // If phone is already verified, just show a simple success message.
        addNotification(t('preferencesPage.welcomeEmailVerified'), 'success', 5000);
      }
      // Clean up the state to prevent the message from showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, addNotification, navigate, user.isPhoneVerified, user.isEmailVerified, location.pathname, t]);

  // Effect to show a persistent warning if email is not verified
  // useEffect(() => {
  //   // Only show if logged in, not loading, and email is not verified.
  //   if (!isLoadingPreferences && !user.isEmailVerified) {
  //     addNotification('Please verify your email address to secure your account and enable all features.', 'warning', 0, true);
  //   }
  // }, [user.isEmailVerified, isLoadingPreferences, addNotification]);

  // Effect to show a persistent info message if email is verified but phone is not
  // useEffect(() => {
  //   // Only show if logged in, not loading, email IS verified, but phone is NOT.
  //   if (!isLoadingPreferences && user.isEmailVerified && !user.isPhoneVerified) {
  //     addNotification(
  //       'Your email is verified! Please complete your profile by verifying your phone number.',
  //       'info',
  //       0, // persistent
  //       true
  //     );
  //   }
  //   // This effect depends on the verification statuses. It will re-evaluate if they change.
  // }, [user.isEmailVerified, user.isPhoneVerified, isLoadingPreferences, addNotification]);

  // Combined effect to fetch preferences and auto-detect language if not set
  useEffect(() => {
    const initializePreferences = async () => {
      setIsLoadingPreferences(true);
      try {
        const response = await apiFetch(`${BASE_URL}/authUser/preferences`, { method: 'GET' }, user.token);
        if (!response.ok) throw new Error(t('preferencesPage.fetchError'));
        const data = await response.json();

        // If report language isn't set in DB, default to an empty string.
        const reportLanguage = data.reportLanguage || '';

        setFormData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          itemsPerPage: data.itemsPerPage,
          subscribe: data.subscribe,
          currency: data.currency || 'ILS',
          phoneNumber: data.phoneNumber || '',
          phoneCountryCode: data.phoneCountryCode || '',
          reportLanguage: reportLanguage,
          investmentProfile: data.investmentProfile || {},
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoadingPreferences(false);
      }
    };

    initializePreferences();
  }, [user.token, addNotification, t]); // Reruns only if the user token changes.

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleProfileSave = (updatedProfile) => {
    // This function is called when the modal saves successfully.
    // We update the local state to reflect the changes immediately.
    setFormData((prev) => ({
      ...prev,
      investmentProfile: updatedProfile,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setError(null);
    //    console.log('Preferences.js: formData:', formData);
    try {
      const response = await apiFetch(
        `${BASE_URL}/authUser/preferences`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        },
        user.token
      );

      if (response.ok) {
        await response.json();
        addNotification(t('preferencesPage.updateSuccess'), 'success', 3000);

        // Update user context with the new preferences
        updateUser({
          // Update the full name in the context for immediate UI feedback
          fullName: `${formData.firstName} ${formData.lastName}`,
          preferences: {
            itemsPerPage: formData.itemsPerPage,
            subscribe: formData.subscribe,
            currency: formData.currency,
            reportLanguage: formData.reportLanguage,
          },
        });

        // If we came from the property page, navigate back
        if (returnToPath) {
          navigate(returnToPath, { state: { from: 'preferences', reopenInquiryModal: true } });
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to update preferences:', errorData);
        const errorMessage = errorData.message || 'An error occurred.';
        setError(errorMessage);
        addNotification(t('preferencesPage.updateError', { error: errorMessage }), 'error', 5000);
      }
    } catch (err) {
      console.error('Network error during preferences update:', err);
      const networkErrorMsg = t('preferencesPage.networkError');
      setError(networkErrorMsg);
      addNotification(networkErrorMsg, 'error', 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (returnToPath) {
      navigate(returnToPath, { state: { from: 'preferences', reopenInquiryModal: false } }); // Don't reopen inquiry modal on cancel
    } else {
      navigate(`/${lang}`);
    }
  };

  const handleBecomeSeller = () => {
    setIsBecomeSellerModalOpen(true);
  };

  const handleConfirmBecomeSeller = async () => {
    setIsBecomeSellerModalOpen(false);
    setIsRequestingSeller(true);
    setSellerRequestStatus('');
    setError(null); // Clear previous general errors

    try {
      const response = await apiFetch(
        `${BASE_URL}/authUser/request-seller-role`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // No body is needed as the backend identifies the user via token
        },
        user.token
      );

      const data = await response.json();

      if (response.ok) {
        const handleLogout = () => {
          logoutUser();
          navigate(`/${lang}/login`);
        };

        // The message is now part of the Trans component inside addNotification
        const message = (
          <Trans
            i18nKey="preferencesPage.becomeSellerSuccess"
            components={{
              strong: <strong />,
            }}
          />
        );

        addNotification(
          message,
          'success',
          0, // duration (persistent)
          true, // isPersistent
          [{ text: t('preferencesPage.buttonOkLogout'), onClick: handleLogout, className: 'primary' }]
        );
      } else {
        const errorMessage = data.message || 'Failed to become a seller.';
        setSellerRequestStatus(errorMessage);
        addNotification(t('preferencesPage.becomeSellerError', { error: errorMessage }), 'error', 5000);
      }
    } catch (err) {
      console.error('Network error during seller request:', err);
      const networkErrorMessage = 'Failed to connect to the server. Please check your network or try again later.';
      setSellerRequestStatus(networkErrorMessage);
      addNotification(networkErrorMessage, 'error', 5000);
    } finally {
      setIsRequestingSeller(false);
    }
  };

  // Debugging: Log user context values relevant to the "Become a Seller" button
  // console.log('[Preferences Debug] User Logged In:', user.isLoggedIn);
  // console.log('[Preferences Debug] User Role from Context:', user.role);
  // console.log('[Preferences Debug] Expected Role.User from Enums:', Role.User);
  // if (user.isLoggedIn) {
  //   console.log('[Preferences Debug] Condition Check (user.role === Role.User):', user.role === Role.User);
  // }

  return (
    <div className="preferences-page-container">
      <div className="preferences-container">
        <h2>{t('preferencesPage.title', { name: user.fullName || 'User' })}</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          {/* First Name and Last Name */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('preferencesPage.firstNameLabel')}</label>
              <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">{t('preferencesPage.lastNameLabel')}</label>
              <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required />
            </div>
          </div>

          {/* Currency Selector */}
          <div className="form-group">
            <label htmlFor="currency">{t('preferencesPage.currencyLabel')}</label>
            <select id="currency" name="currency" value={formData.currency} onChange={handleChange}>
              <option value="ILS">{t('preferencesPage.currencyILS')}</option>
              <option value="USD">{t('preferencesPage.currencyUSD')}</option>
              <option value="EUR">{t('preferencesPage.currencyEUR')}</option>
            </select>
          </div>

          {/* Preferred Language Selector */}
          <div className="form-group">
            <label htmlFor="reportLanguage">{t('preferencesPage.reportLanguageLabel')}</label>
            <select id="reportLanguage" name="reportLanguage" value={formData.reportLanguage} onChange={handleChange} className={!formData.reportLanguage ? 'missing-field' : ''}>
              <option value="">{t('preferencesPage.selectLanguage')}</option>
              <option value="en">{t('preferencesPage.langEN')}</option>
              <option value="he">{t('preferencesPage.langHE')}</option>
              <option value="ru">{t('preferencesPage.langRU')}</option>
              <option value="es">{t('preferencesPage.langES')}</option>
            </select>
          </div>

          {/* Items Per Page */}
          {user.isAdmin && (
            <div className="form-group">
              <label htmlFor="itemsPerPage">{t('preferencesPage.itemsPerPageLabel')}</label>
              <input type="number" id="itemsPerPage" name="itemsPerPage" value={formData.itemsPerPage} onChange={handleChange} min="3" max="50" />
            </div>
          )}

          {/* Subscribe */}
          <div className="form-group">
            <label htmlFor="subscribe">{t('preferencesPage.subscribeLabel')}</label>
            <div className="radio-container">
              <input type="checkbox" id="subscribe" name="subscribe" checked={formData.subscribe} onChange={handleChange} />
            </div>
            <small className="form-text text-muted">{t('preferencesPage.subscribeDescription')}</small>
          </div>

          <div className="button-group">
            <button type="button" className="cancel-btn" onClick={handleCancel} disabled={isSubmitting}>
              {returnToPath ? t('preferencesPage.buttonBack') : t('preferencesPage.buttonCancel')}
            </button>
            <button type="submit" className="subscribe-btn" disabled={isSubmitting}>
              {t('preferencesPage.buttonUpdate')}
            </button>
          </div>
        </form>

        {/* Email Verification Section */}
        {isLoadingPreferences ? (
          <div className="verification-container-loading">
            <p>{t('preferencesPage.loadingEmail')}</p>
          </div>
        ) : (
          <div className={!user.isEmailVerified ? 'verification-needed' : ''}>
            <EmailVerification />
          </div>
        )}

        {/* Phone Verification Section */}
        {isLoadingPreferences ? (
          <div className="verification-container-loading">
            <p>{t('preferencesPage.loadingPhone')}</p>
          </div>
        ) : (
          <div className={!user.isPhoneVerified ? 'verification-needed' : ''}>
            <PhoneVerification initialPhoneNumber={formData.phoneNumber} initialCountryCode={formData.phoneCountryCode} />
          </div>
        )}

        {/* Investment Profile Section */}
        <div className="investment-profile-section">
          <h4>{t('preferencesPage.investmentProfileTitle')}</h4>
          <p>{t('preferencesPage.investmentProfileDescription')}</p>
          <button onClick={() => setIsProfileModalOpen(true)} className="action-btn">
            {t('preferencesPage.buttonEditProfile')}
          </button>
        </div>

        {/* Become a Seller Section */}
        {user.role === Role.User && (
          <div className="become-seller-section form-group">
            {' '}
            {/* Added form-group for consistent spacing */}
            <h4>{t('preferencesPage.becomeSellerTitle')}</h4>
            <p>{t('preferencesPage.becomeSellerDescription')}</p>
            <button
              onClick={handleBecomeSeller}
              className="subscribe-btn" // You might want a different class for styling
              disabled={isRequestingSeller || isSubmitting} // Also disable if main form is submitting
            >
              {isRequestingSeller ? t('preferencesPage.buttonProcessing') : t('preferencesPage.buttonBecomeSeller')}
            </button>
            {sellerRequestStatus && <p className={sellerRequestStatus.includes('Failed') || sellerRequestStatus.includes('error') ? 'error-message' : 'success-message'}>{sellerRequestStatus}</p>}
          </div>
        )}

        {/* Account Management Section */}
        <div className="account-actions-section">
          <h4>{t('preferencesPage.accountManagementTitle')}</h4>
          <div className="account-actions-links">
            <button onClick={() => navigate(`/${lang}/change-password`)} className="action-btn">
              {t('preferencesPage.buttonChangePassword')}
            </button>
            <button onClick={() => navigate(`/${lang}/delete-account`)} className="delete-btn">
              {t('preferencesPage.buttonDeleteAccount')}
            </button>
          </div>
        </div>
      </div>

      <InvestmentProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} initialProfileData={formData.investmentProfile} onSave={handleProfileSave} mode="fill" />
      <BecomeSellerModal isOpen={isBecomeSellerModalOpen} onClose={() => setIsBecomeSellerModalOpen(false)} onConfirm={handleConfirmBecomeSeller} />
    </div>
  );
};

export default Preferences;
